/* PuraGold homepage. GSAP contexts and event listeners follow Shopify section lifecycles. */
(() => {
  'use strict';
  if (window.PuraGoldHome) return;
  const instances = new Map();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let motionPaused = false;
  let lenis, lenisTick, lenisFrame = 0, motionButton;
  const motionAllowed = () => !motionPaused && !reducedMotion.matches && !window.Shopify?.designMode;
  const hasGSAP = () => Boolean(window.gsap && window.ScrollTrigger);
  if (hasGSAP()) window.gsap.registerPlugin(window.ScrollTrigger);

  function splitHeading(element) {
    if (element.querySelector('.pg-char')) return () => {};
    const original = element.innerHTML;
    const oldLabel = element.getAttribute('aria-label');
    element.setAttribute('aria-label', element.innerText.replace(/\s+/g, ' ').trim());
    const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(node => {
      const fragment = document.createDocumentFragment();
      // Keep Latin words together while allowing Chinese text to wrap character by character.
      const tokens = node.textContent.match(/[\p{Script=Latin}\p{M}\p{N}]+(?:['’®-][\p{Script=Latin}\p{M}\p{N}]+)*[®]?|\s+|./gu) || [];
      for (const token of tokens) {
        if (/^\s+$/.test(token)) {
          fragment.append(document.createTextNode(token));
          continue;
        }
        const word = /[\p{Script=Latin}\p{N}]/u.test(token) ? document.createElement('span') : null;
        if (word) word.className = 'pg-word';
        for (const character of Array.from(token)) {
        const span = document.createElement('span');
        span.className = 'pg-char';
        span.setAttribute('aria-hidden', 'true');
        span.textContent = character === ' ' ? '\u00a0' : character;
        (word || fragment).append(span);
        }
        if (word) fragment.append(word);
      }
      node.replaceWith(fragment);
    });
    return () => {
      element.innerHTML = original;
      if (oldLabel === null) element.removeAttribute('aria-label');
      else element.setAttribute('aria-label', oldLabel);
    };
  }

  function goldDust(canvas, root) {
    const ctx = canvas.getContext('2d');
    if (!ctx) return () => {};
    const settingScale = (value, min, max) => {
      const number = value === undefined || value === '' ? 100 : Number(value);
      return Math.min(max, Math.max(min, Number.isFinite(number) ? number : 100)) / 100;
    };
    const sizeScale = settingScale(root.dataset.dustSize, 50, 200);
    const brightness = settingScale(root.dataset.dustBrightness, 0, 150);
    const color = /^\d{1,3},\d{1,3},\d{1,3}$/.test(root.dataset.dustColor || '') ? root.dataset.dustColor : '252,225,182';
    let width, height, frame = 0, visible = false, lastTime = 0;
    let particles = [];
    const particle = (initial) => ({x: Math.random() * width, y: initial ? Math.random() * height : height + 10, radius: (Math.random() * 2.1 + 1) * sizeScale, speed: Math.random() * .35 + .12, opacity: Math.random() * .35 + .55, phase: Math.random() * Math.PI * 2});
    const size = () => {
      width = root.clientWidth; height = root.clientHeight;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      canvas.width = Math.round(width * dpr); canvas.height = Math.round(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      particles = Array.from({length: width < 750 ? 30 : 70}, () => particle(true));
    };
    const loop = (time) => {
      const delta = Math.min((time - (lastTime || time)) / 16.67, 2); lastTime = time;
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, i) => {
        p.y -= p.speed * delta; p.x += Math.sin(time / 1600 + p.phase) * .2 * delta;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${color},${Math.min(1, p.opacity * brightness * (.8 + .2 * Math.sin(time / 700 + p.phase * 3)))})`; ctx.fill();
        if (p.y < -10) particles[i] = particle(false);
      });
      frame = requestAnimationFrame(loop);
    };
    const update = () => {
      cancelAnimationFrame(frame); lastTime = 0;
      if (visible && !document.hidden) frame = requestAnimationFrame(loop);
    };
    const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; update(); });
    const resize = new ResizeObserver(size);
    size(); observer.observe(root); resize.observe(root);
    document.addEventListener('visibilitychange', update);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); resize.disconnect(); document.removeEventListener('visibilitychange', update); ctx.clearRect(0, 0, width, height); };
  }

  class HomeSection {
    constructor(root) {
      this.root = root;
      this.events = new AbortController();
      this.cleanups = [];
      this.index = 0;
      this.slides = [...root.querySelectorAll('[data-pg-slide]')];
      this.controls = [...root.querySelectorAll('[data-pg-slide-button]')];
      this.manualPause = false;
      this.bindControls();
      this.setupMotion();
    }
    listen(target, type, fn) { target?.addEventListener(type, fn, {signal: this.events.signal}); }
    setupMotion() {
      this.clearMotion();
      this.syncHeroLayout();
      this.syncHeroVideo();
      this.syncDust();
      this.setupLabelFloat();
      if (!hasGSAP() || !motionAllowed()) return;
      const {root} = this;
      const gsap = window.gsap;
      if (root.dataset.animate === 'true') {
        root.querySelectorAll('[data-pg-split]').forEach(el => this.cleanups.push(splitHeading(el)));
      }
      this.context = gsap.context(() => {
        if (root.dataset.animate === 'true') {
          if (root.classList.contains('pg-page-intro') ) {
            const introMedia = root.querySelector('.pg-page-intro-media .pg-picture, .pg-page-intro-media video');
            if (introMedia) gsap.from(introMedia, {scale: 1.08, duration: 1.8, ease: 'power2.out', clearProps: 'transform'});
            gsap.from(root.querySelectorAll('.pg-page-intro-copy > :not(h1)'), {y: 24, autoAlpha: 0, duration: 1, stagger: .12, delay: .25, clearProps: 'transform,opacity,visibility'});
          }
          const botanicalPhoto = root.querySelector('.pg-botanicals-photo');
          if (botanicalPhoto) gsap.from(botanicalPhoto, {y: 28, autoAlpha: 0, duration: 1.2, ease: 'power2.out', clearProps: 'transform,opacity,visibility', scrollTrigger: {trigger: botanicalPhoto, start: 'top 88%', once: true}});
          root.querySelectorAll('[data-pg-count]').forEach(el => {
            const original = el.textContent.trim();
            if (!/^\d[\d,]*(\.\d+)?$/.test(original)) return;
            const target = Number(original.replaceAll(',', ''));
            const decimals = original.includes('.') ? original.split('.')[1].length : 0;
            const format = value => value.toLocaleString('en-US', {useGrouping: original.includes(','), minimumFractionDigits: decimals, maximumFractionDigits: decimals});
            const state = {value: 0};
            this.cleanups.push(() => {el.textContent = original;});
            gsap.to(state, {value: target, duration: 1.8, ease: 'power2.out', scrollTrigger: {trigger: el, start: 'top 90%', once: true}, onUpdate: () => {el.textContent = format(state.value);}, onComplete: () => {el.textContent = original;}});
          });
          if (this.slides.length) this.animateSlide();
          else {
            root.querySelectorAll('[data-pg-split]').forEach(el => gsap.from(el.querySelectorAll('.pg-char'), {yPercent: 105, autoAlpha: 0, duration: .95, stagger: .045, ease: 'power3.out', scrollTrigger: {trigger: el, start: 'top 90%', once: true}}));
            root.querySelectorAll('[data-pg-reveal]').forEach(el => gsap.from(el, {y: 46, autoAlpha: 0, duration: 1.05, ease: 'power3.out', scrollTrigger: {trigger: el, start: 'top 90%', once: true}}));
          }
        }
        if (root.dataset.parallax === 'true') {
          this.mm = gsap.matchMedia();
          this.mm.add({desktop: '(min-width: 900px)', mobile: '(max-width: 899px)'}, ({conditions}) => {
            root.querySelectorAll('[data-pg-parallax]').forEach(frame => {
              if (frame.closest('[data-media-type="video"]')) return;
              const target = frame.classList.contains('pg-hero-bg') ? frame : frame.querySelector('.pg-picture');
              if (!target) return;
              const hero = frame.classList.contains('pg-hero-bg');
              gsap.fromTo(target, {yPercent: hero ? 0 : -5}, {yPercent: hero ? (conditions.desktop ? 18 : 9) : 5, ease: 'none', scrollTrigger: {trigger: hero ? root : frame, start: hero ? 'top top' : 'top bottom', end: 'bottom top', scrub: .7}});
            });
          });
        }
        const seal = root.querySelector('.pg-seal svg');
        if (seal) gsap.to(seal, {rotation: 360, duration: 16, repeat: -1, ease: 'none', scrollTrigger: {trigger: root, start: 'top bottom', end: 'bottom top', toggleActions: 'play pause play pause'}});
        const hint = root.querySelector('.pg-scroll-hint i');
        if (hint) gsap.fromTo(hint, {scaleY: .05}, {scaleY: 1, duration: 1.1, repeat: -1, yoyo: true, ease: 'power1.inOut', scrollTrigger: {trigger: root, start: 'top bottom', end: 'bottom top', toggleActions: 'play pause play pause'}});
      }, root);
      this.startAutoplay();
    }
    setupLabelFloat() {
      const {root} = this;
      const stage = root.querySelector('.pg-botanicals-image');
      if (!stage || root.dataset.float !== 'true' || !motionAllowed()) return;
      // CSS owns every animation frame. JS only pauses on visibility changes.
      root.classList.add('pg-float-enabled');
      let visible = false;
      const sync = () => root.classList.toggle('pg-float-running', visible && !document.hidden);
      const observer = new IntersectionObserver(entries => { visible = entries[0].isIntersecting; sync(); });
      observer.observe(stage);
      document.addEventListener('visibilitychange', sync);
      this.cleanups.push(() => {
        observer.disconnect();
        document.removeEventListener('visibilitychange', sync);
        root.classList.remove('pg-float-enabled', 'pg-float-running');
      });
    }
    animateSlide() {
      if (!hasGSAP() || !motionAllowed() || this.root.dataset.animate !== 'true') return;
      const slide = this.slides[this.index];
      if (!slide) return;
      this.heroTimeline?.kill();
      this.heroTimeline = window.gsap.timeline({defaults: {ease: 'power3.out'}})
        .fromTo(slide.querySelectorAll('[data-pg-hero-reveal]'), {y: 30, autoAlpha: 0}, {y: 0, autoAlpha: 1, duration: 1, stagger: .13, clearProps: 'opacity,visibility,transform'}, .15)
        .fromTo(slide.querySelectorAll('.pg-char'), {yPercent: 100, autoAlpha: 0}, {yPercent: 0, autoAlpha: 1, duration: 1.1, stagger: .045, clearProps: 'opacity,visibility,transform'}, .2);
    }
    showSlide(index) {
      if (index === this.index || !this.slides[index]) return;
      this.heroTimeline?.kill();
      this.slides.forEach((slide, i) => {
        slide.hidden = i !== index;
        slide.classList.toggle('is-active', i === index);
        if (i !== index) slide.querySelectorAll('video').forEach(video => video.pause());
      });
      this.index = index;
      this.syncHeroLayout();
      this.syncHeroVideo();
      this.syncDust();
      this.controls.forEach((button, i) => button.setAttribute('aria-pressed', String(i === index)));
      if (this.context) this.context.add(() => this.animateSlide());
    }
    startAutoplay() {
      clearInterval(this.interval);
      if (!motionAllowed() || this.manualPause || this.root.dataset.autoplay !== 'true' || this.slides.length < 2) return;
      this.interval = setInterval(() => {
        const bounds = this.root.getBoundingClientRect();
        if (!document.hidden && bounds.bottom > 0 && bounds.top < innerHeight && !this.root.matches(':hover') && !this.root.contains(document.activeElement)) this.showSlide((this.index + 1) % this.slides.length);
      }, Number(this.root.dataset.interval || 8) * 1000);
    }
    bindControls() {
      this.root.querySelectorAll('.pg-hero-video').forEach(video => {
        const slide = video.closest('[data-pg-slide]');
        this.listen(video, 'loadedmetadata', () => this.syncHeroLayout());
        this.listen(video, 'canplaythrough', () => this.syncHeroVideo());
        this.listen(video, 'waiting', () => slide?.classList.add('is-video-buffering'));
        this.listen(video, 'playing', () => slide?.classList.remove('is-video-buffering'));
      });
      this.root.querySelectorAll('[data-pg-video-toggle]').forEach(button => {
        const video = button.closest('[data-pg-slide]')?.querySelector('video');
        if (!video) return;
        this.listen(button, 'click', () => { if (video.paused) video.play().catch(() => {}); else video.pause(); });
        this.listen(video, 'play', () => button.setAttribute('aria-pressed', 'true'));
        this.listen(video, 'pause', () => button.setAttribute('aria-pressed', 'false'));
      });
      this.controls.forEach((button, i) => this.listen(button, 'click', () => { this.showSlide(i); this.startAutoplay(); }));
      const pause = this.root.querySelector('[data-pg-autoplay]');
      this.listen(pause, 'click', () => { this.manualPause = !this.manualPause; pause.setAttribute('aria-pressed', String(this.manualPause)); this.startAutoplay(); });
      this.dialog = this.root.querySelector('dialog');
      if (!this.dialog) return;
      const open = this.root.querySelector('[data-pg-open-video]');
      const close = this.root.querySelector('[data-pg-close-video]');
      this.listen(open, 'click', () => {
        this.previousFocus = document.activeElement;
        const mount = this.dialog.querySelector('[data-pg-video-mount]');
        mount.replaceChildren(this.dialog.querySelector('template').content.cloneNode(true));
        this.dialog.showModal(); lenis?.stop(); close.focus();
      });
      this.listen(close, 'click', () => this.dialog.close());
      this.listen(this.dialog, 'click', event => { if (event.target === this.dialog && (event.clientY < this.dialog.getBoundingClientRect().top || event.clientY > this.dialog.getBoundingClientRect().bottom || event.clientX < this.dialog.getBoundingClientRect().left || event.clientX > this.dialog.getBoundingClientRect().right)) this.dialog.close(); });
      this.listen(this.dialog, 'close', () => {
        this.dialog.querySelector('[data-pg-video-mount]').replaceChildren();
        lenis?.start(); this.previousFocus?.focus();
      });
    }
    clearMotion() {
      this.dustCleanup?.(); this.dustCleanup = null;
      clearInterval(this.interval);
      this.heroTimeline?.kill();
      this.mm?.revert(); this.mm = null;
      this.context?.revert(); this.context = null;
      this.cleanups.splice(0).reverse().forEach(cleanup => cleanup());
      this.root.querySelectorAll('[data-pg-hero-reveal]').forEach(el => { el.style.removeProperty('opacity'); el.style.removeProperty('visibility'); el.style.removeProperty('transform'); });
    }
    syncDust() {
      this.dustCleanup?.(); this.dustCleanup = null;
      const canvas = this.root.querySelector('.pg-dust');
      if (!canvas) return;
      canvas.hidden = this.slides[this.index]?.dataset.mediaType === 'video' || !motionAllowed();
      if (!canvas.hidden) this.dustCleanup = goldDust(canvas, this.root);
    }
    syncHeroLayout() {
      if (!this.slides.length) return;
      const slide = this.slides[this.index];
      const video = slide?.querySelector('video');
      const videoActive = slide?.dataset.mediaType === 'video' && Boolean(video);
      this.root.classList.toggle('pg-hero--video-active', videoActive);
      if (!videoActive) return;
      const intrinsicRatio = video.videoWidth > 0 && video.videoHeight > 0 ? video.videoWidth / video.videoHeight : 0;
      const configuredRatio = Number(slide.dataset.videoAspect);
      const ratio = intrinsicRatio || (Number.isFinite(configuredRatio) && configuredRatio > 0 ? configuredRatio : 16 / 9);
      this.root.style.setProperty('--pg-video-aspect', String(ratio));
      refresh();
    }
    syncHeroVideo() {
      this.slides.forEach((slide, i) => {
        const video = slide.querySelector('video');
        if (!video) return;
        const active = i === this.index && motionAllowed();
        video.preload = active ? 'auto' : 'metadata';
        slide.classList.toggle('is-video-buffering', active && video.readyState < HTMLMediaElement.HAVE_ENOUGH_DATA);
        if (!active) { video.pause(); return; }
        if (video.networkState === HTMLMediaElement.NETWORK_EMPTY) video.load();
        if (video.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA) requestAnimationFrame(() => {
          if (this.slides[this.index] === slide && motionAllowed()) video.play().catch(() => {});
        });
        else video.pause();
      });
    }
    destroy() {
      if (this.dialog?.open) this.dialog.close();
      this.clearMotion(); this.events.abort();
    }
  }

  function setupGlobalMotion() {
    if (lenisTick && hasGSAP()) window.gsap.ticker.remove(lenisTick);
    cancelAnimationFrame(lenisFrame); lenisFrame = 0;
    lenis?.destroy(); lenis = null; window.PuraGoldLenis = null;
    const hero = document.querySelector('[data-pg-section="hero"]');
    const smoothEnabled = hero ? hero.dataset.smooth === 'true' : true;
    if (!smoothEnabled || !motionAllowed() || !window.Lenis) return;
    // The body grows when lazy media and the footer load; the root viewport may not.
    lenis = new window.Lenis({content: document.body, duration: 1.16, smoothWheel: true, syncTouch: false, wheelMultiplier: .92, easing: t => Math.min(1, 1.001 - Math.pow(2, -10 * t)), prevent: node => Boolean(node.closest?.('dialog, .menu-drawer, .drawer, [data-lenis-prevent]'))});
    if (hasGSAP()) {
      lenis.on('scroll', window.ScrollTrigger.update);
      lenisTick = time => lenis?.raf(time * 1000);
      window.gsap.ticker.add(lenisTick);
    } else {
      const frame = time => { lenis?.raf(time); lenisFrame = requestAnimationFrame(frame); };
      lenisFrame = requestAnimationFrame(frame);
    }
    window.PuraGoldLenis = lenis;

  }
  let refreshFrame = 0;
  function refresh() {
    if (refreshFrame) return;
    refreshFrame = requestAnimationFrame(() => {
      refreshFrame = 0;
      if (hasGSAP()) window.ScrollTrigger.refresh();
      lenis?.resize();
    });
  }
  function mount(parent) {
    const roots = parent.matches?.('[data-pg-section]') ? [parent] : parent.querySelectorAll('[data-pg-section]');
    roots.forEach(root => { if (!instances.has(root)) instances.set(root, new HomeSection(root)); });
    setupGlobalMotion(); refresh();
  }
  function rebuildMotion() {
    instances.forEach(instance => instance.setupMotion()); setupGlobalMotion(); refresh();
    if (motionButton) motionButton.setAttribute('aria-pressed', String(motionPaused));
  }
  reducedMotion.addEventListener('change', rebuildMotion);
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((instance, root) => { if (event.target.contains(root)) { instance.destroy(); instances.delete(root); } });
    setupGlobalMotion(); refresh();
  });
  document.addEventListener('shopify:block:select', event => {
    const slide = event.target.closest('[data-pg-slide]');
    if (slide) { const instance = instances.get(slide.closest('[data-pg-section]')); instance?.showSlide(instance.slides.indexOf(slide)); }
  });
  document.addEventListener('shopify:section:reorder', refresh);
  document.addEventListener('load', event => { if (event.target.tagName === 'IMG') refresh(); }, true);
  document.addEventListener('loadedmetadata', event => { if (event.target.tagName === 'VIDEO') refresh(); }, true);
  document.fonts?.ready.then(refresh);
  window.addEventListener('load', refresh, {once: true});
  mount(document);
  const hero = document.querySelector('[data-pg-section="hero"]');
  if (hero && !window.Shopify?.designMode && hasGSAP()) {
    motionButton = document.createElement('button'); motionButton.type = 'button'; motionButton.className = 'pg-motion-toggle';
    motionButton.textContent = hero.querySelector('[data-pg-motion-label]')?.textContent || '暫停動效'; motionButton.setAttribute('aria-pressed', 'false');
    motionButton.addEventListener('click', () => { motionPaused = !motionPaused; rebuildMotion(); });
    document.body.append(motionButton);
  }
  window.PuraGoldHome = {instances, refresh};
})();
